import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { Typography } from '@euler/app/components/typography';
import { MemberCell } from '@euler/app/flows/common/components/MemberCell';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useMemberList } from '@euler/functions/useMemberList';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType } from '@euler/model/enum';
import { BottomSheetFlatList, BottomSheetModal } from '@gorhom/bottom-sheet';
import { Suspense, memo, useEffect, useState } from 'react';
import { FlatList, Platform, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const Header = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <Center
      css={`
        padding: ${rpx(10)}px 0;
        border-bottom-width: 1px;
        border-bottom-color: #f1f1f1;
        height: ${rpx(48)}px;
        background-color: #fff;
      `}
    >
      <Typography.Label size={rpx(18)} lineHeight={28} color="#333" medium>
        选择施工技师
      </Typography.Label>
    </Center>
  );
});

const Content = memo(
  ({
    onSelect,
  }: {
    selected?: number;
    onSelect?: (member: OrgMember) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const members = useMemberList(OrgUserRoleType.Technicians)!;

    const ListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    return (
      <ListComponent
        data={members}
        contentContainerStyle={{
          paddingLeft: rpx(15),
          paddingRight: rpx(15),
          paddingBottom: insets.bottom + rpx(15),
        }}
        keyExtractor={x => String(x.id)}
        ListHeaderComponent={<Header />}
        stickyHeaderIndices={[0]}
        renderItem={({ item, index }) => (
          <MemberCell
            member={item}
            first={index === 0}
            last={index === members.length - 1}
            onPress={onSelect}
            isRoleLabelVisible
          />
        )}
      />
    );
  },
);

const Fallback = () => {
  const { rpx } = useResponsiveContext();
  const { navBarHeight } = useSystemMetrics();
  return (
    <ScrollView
      css={`
        flex: 1;
      `}
      contentContainerStyle={[
        {
          flexGrow: 1,
          paddingTop: navBarHeight + rpx(15),
          justifyContent: 'flex-start',
          alignItems: 'center',
        },
      ]}
    >
      <DefaultLoader />
    </ScrollView>
  );
};

export const ConstructionJobTechnicianBottomSheetModal = memo(
  ({
    visible,
    technicianId,
    onSelect,
    onDismiss,
  }: {
    visible?: boolean;
    technicianId?: number;
    editable?: boolean;
    onSelect?: (technician: OrgMember) => void;
    onDismiss?: () => void;
  }) => {
    const [show, setShow] = useState(visible);
    const { navBarHeight } = useSystemMetrics();

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show: visible,
        topMargin: navBarHeight,
        minSnapPoint: '50%',
        mediumSnapPoint: '75%',
        index: 1,
        enableDismissOnClose: true,
        enablePanDownToClose: true,
        dismissWhenPressBackdrop: true,
        onDismiss,
      });

    useEffect(() => {
      if (visible) setShow(true);
    }, [visible]);

    return (
      <BottomSheetModal {...bottomSheetProps} ref={bottomSheetModalRef}>
        {show && (
          <Suspense fallback={<Fallback />}>
            <Content selected={technicianId} onSelect={onSelect} />
          </Suspense>
        )}
      </BottomSheetModal>
    );
  },
);
